import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, MapPin, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { adminStoresAPI } from '../../utils/api';
import { toast } from '../../hooks/use-toast';
import { Toaster } from '../ui/toaster';

const StoresManagement = () => {
  const [stores, setStores] = useState([]);
  const [selectedStore, setSelectedStore] = useState(null);
  const [isCreateMode, setIsCreateMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    category: '',
    description: '',
    is_active: true,
    map_x: '',
    map_y: '',
    logo: '',
    color: '',
    valid_until: '',
  });

  useEffect(() => {
    loadStores();
  }, []);

  const loadStores = async () => {
    try {
      setLoading(true);
      const response = await adminStoresAPI.getAll();
      setStores(response.data || []);
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudieron cargar las tiendas",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      slug: '',
      category: '',
      description: '',
      is_active: true,
      map_x: '',
      map_y: '',
      logo: '',
      color: '',
      valid_until: '',
    });
    setSelectedStore(null);
    setIsCreateMode(false);
  };

  const handleCreate = () => {
    resetForm();
    setIsCreateMode(true);
  };

  const handleEdit = (store) => {
    setSelectedStore(store);
    setIsCreateMode(false);
    setFormData({
      name: store.name || '',
      slug: store.slug || '',
      category: store.category || '',
      description: store.description || '',
      is_active: store.isActive ?? store.isParticipating ?? true,
      map_x: store.mapX?.toString() || store.mapPosition?.x?.replace('%', '') || '',
      map_y: store.mapY?.toString() || store.mapPosition?.y?.replace('%', '') || '',
      logo: store.logoUrl || store.logo || '',
      color: store.color || '',
      valid_until: store.validUntil || '',
    });
  };

  const handleDelete = async (storeId, storeName) => {
    if (!window.confirm(`¿Estás seguro de que deseas eliminar la tienda "${storeName}"?`)) {
      return;
    }

    try {
      await adminStoresAPI.delete(storeId);
      setStores(stores.filter(s => s.id !== storeId));
      toast({
        title: "Tienda eliminada",
        description: "La tienda ha sido eliminada correctamente",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.error || error.response?.data?.message || "No se pudo eliminar la tienda",
        variant: "destructive"
      });
    }
  };

  const handleSave = async () => {
    // Validaciones básicas
    if (!formData.name || !formData.category) {
      toast({
        title: "Error de validación",
        description: "Por favor completa los campos requeridos (Nombre, Categoría)",
        variant: "destructive"
      });
      return;
    }

    // Validar coordenadas si se proporcionan
    if (formData.map_x && (parseFloat(formData.map_x) < 0 || parseFloat(formData.map_x) > 100)) {
      toast({
        title: "Error de validación",
        description: "La coordenada X debe estar entre 0 y 100",
        variant: "destructive"
      });
      return;
    }
    if (formData.map_y && (parseFloat(formData.map_y) < 0 || parseFloat(formData.map_y) > 100)) {
      toast({
        title: "Error de validación",
        description: "La coordenada Y debe estar entre 0 y 100",
        variant: "destructive"
      });
      return;
    }

    try {
      setSaving(true);
      const payload = {
        name: formData.name,
        slug: formData.slug || undefined,
        category: formData.category,
        benefit: formData.benefit || undefined,
        description: formData.description || undefined,
        is_active: formData.is_active,
        map_x: formData.map_x ? parseFloat(formData.map_x) : undefined,
        map_y: formData.map_y ? parseFloat(formData.map_y) : undefined,
        logo: formData.logo || undefined,
        color: formData.color || undefined,
        valid_until: formData.valid_until || undefined,
      };

      let response;
      if (isCreateMode) {
        response = await adminStoresAPI.create(payload);
        setStores([...stores, response.data]);
        toast({
          title: "Tienda creada",
          description: "La tienda ha sido creada correctamente",
        });
      } else {
        response = await adminStoresAPI.update(selectedStore.id, payload);
        setStores(stores.map(s => 
          s.id === selectedStore.id ? { ...s, ...response.data } : s
        ));
        toast({
          title: "Cambios guardados",
          description: "La tienda ha sido actualizada correctamente",
        });
      }
      resetForm();
    } catch (error) {
      const errorMessage = error.response?.data?.error || 
                          error.response?.data?.message || 
                          error.response?.data?.errors?.[Object.keys(error.response.data.errors || {})[0]]?.[0] ||
                          "No se pudieron guardar los cambios";
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const toggleStoreStatus = async (storeId, currentStatus) => {
    try {
      await adminStoresAPI.toggle(storeId, !currentStatus);
      setStores(stores.map(s => 
        s.id === storeId ? { ...s, isParticipating: !currentStatus, isActive: !currentStatus } : s
      ));
      toast({
        title: "Estado actualizado",
        description: "El estado de la tienda ha sido modificado",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.error || "No se pudo actualizar el estado",
        variant: "destructive"
      });
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <p className="text-white">Cargando tiendas...</p>
      </div>
    );
  }

  return (
    <>
      <div className="p-8">
        <div className="max-w-6xl mx-auto space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-3xl font-bold text-white mb-2">Gestión de tiendas</h2>
              <p className="text-[#A2A2A2]">Administra las tiendas participantes del outlet</p>
            </div>
            <Button
              onClick={handleCreate}
              className="bg-white text-black hover:bg-white/90 rounded-full px-6 py-3 flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              <span>Nueva tienda</span>
            </Button>
          </div>

          {/* Stores List */}
          <div className="space-y-3">
            {stores.length === 0 ? (
              <div className="text-center py-12 text-[#A2A2A2]">
                <p>No hay tiendas registradas</p>
                <p className="text-sm mt-2">Haz clic en "Nueva tienda" para crear una</p>
              </div>
            ) : (
              stores.map((store) => (
                <div
                  key={store.id}
                  className="bg-black border border-white/10 rounded-3xl p-6 hover:border-white/20 transition-all"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-xl font-semibold text-white">{store.name}</h3>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          (store.isActive ?? store.isParticipating)
                            ? 'bg-white/10 text-white'
                            : 'bg-white/5 text-[#A2A2A2]'
                        }`}>
                          {(store.isActive ?? store.isParticipating) ? 'Activa' : 'Inactiva'}
                        </span>
                        {store.isFeatured && (
                          <span className="px-3 py-1 rounded-full text-xs font-medium bg-yellow-500/20 text-yellow-500">
                            Destacada
                          </span>
                        )}
                      </div>
                      <p className="text-[#A2A2A2] text-sm mb-1">{store.category}</p>
                      
                      {(store.mapX !== null && store.mapX !== undefined) || 
                       (store.mapY !== null && store.mapY !== undefined) || 
                       store.mapPosition ? (
                        <div className="flex items-center gap-2 text-[#A2A2A2] text-sm">
                          <MapPin className="w-4 h-4" />
                          <span>
                            Posición: {store.mapX ?? store.mapPosition?.x ?? 'N/A'}, {store.mapY ?? store.mapPosition?.y ?? 'N/A'}
                          </span>
                        </div>
                      ) : null}
                    </div>

                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={store.isActive ?? store.isParticipating}
                          onCheckedChange={() => toggleStoreStatus(store.id, store.isActive ?? store.isParticipating)}
                        />
                      </div>
                      <Button
                        onClick={() => handleEdit(store)}
                        variant="outline"
                        className="border-white/10 text-white bg-transparent hover:bg-white/10 rounded-full"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        onClick={() => handleDelete(store.id, store.name)}
                        variant="outline"
                        className="border-red-500/50 text-red-500 bg-transparent hover:bg-red-500/10 rounded-full"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Create/Edit Store Dialog */}
      <Dialog open={!!selectedStore || isCreateMode} onOpenChange={resetForm}>
        <DialogContent className="bg-black border-white/20 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">
              {isCreateMode ? 'Crear nueva tienda' : 'Editar tienda'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-white">Nombre *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="bg-white/5 border-white/10 text-white rounded-2xl h-12"
                  placeholder="Ej: Nike"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white">Slug (opcional)</Label>
                <Input
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                  className="bg-white/5 border-white/10 text-white rounded-2xl h-12"
                  placeholder="Se genera automáticamente si se deja vacío"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-white">Categoría *</Label>
              <Input
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="bg-white/5 border-white/10 text-white rounded-2xl h-12"
                placeholder="Ej: Deportivo"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-white">Descripción</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="bg-white/5 border-white/10 text-white rounded-2xl min-h-[100px]"
                placeholder="Descripción de la tienda y sus beneficios..."
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-white">Coordenada X (%)</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={formData.map_x}
                  onChange={(e) => setFormData({ ...formData, map_x: e.target.value })}
                  className="bg-white/5 border-white/10 text-white rounded-2xl h-12"
                  placeholder="0-100"
                />
                <p className="text-xs text-[#A2A2A2]">
                  Usa valores de 0 a 100, donde 0,0 es la esquina superior izquierda del mapa y 100,100 la esquina inferior derecha.
                </p>
              </div>
              <div className="space-y-2">
                <Label className="text-white">Coordenada Y (%)</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={formData.map_y}
                  onChange={(e) => setFormData({ ...formData, map_y: e.target.value })}
                  className="bg-white/5 border-white/10 text-white rounded-2xl h-12"
                  placeholder="0-100"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-white">Logo (URL)</Label>
                <Input
                  type="url"
                  value={formData.logo}
                  onChange={(e) => setFormData({ ...formData, logo: e.target.value })}
                  className="bg-white/5 border-white/10 text-white rounded-2xl h-12"
                  placeholder="https://ejemplo.com/logo.png"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white">Color</Label>
                <Input
                  value={formData.color}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  className="bg-white/5 border-white/10 text-white rounded-2xl h-12"
                  placeholder="#FFFFFF o nombre de color"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-white">Vigencia hasta</Label>
              <Input
                type="date"
                value={formData.valid_until}
                onChange={(e) => setFormData({ ...formData, valid_until: e.target.value })}
                className="bg-white/5 border-white/10 text-white rounded-2xl h-12"
              />
            </div>

            <div className="flex items-center gap-2">
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
              <Label className="text-white">Activa / Participando</Label>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                onClick={resetForm}
                variant="outline"
                className="flex-1 border-white/10 text-white bg-transparent hover:bg-white/10 rounded-full py-6"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 bg-white text-black hover:bg-white/90 rounded-full py-6 transition-all hover:scale-105 active:scale-95 disabled:opacity-50"
              >
                {saving ? 'Guardando...' : isCreateMode ? 'Crear tienda' : 'Guardar cambios'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Toaster />
    </>
  );
};

export default StoresManagement;
